import {CampaignEnvironment} from 'common/src/modules/campaignPlacements/placements.types';
import {placementSchema} from '../placementsManagementTool.schema';
import {z} from 'zod';
import {CampaignType, PlacementCustomParamName} from 'common/src/modules/campaigns/campaigns.types';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import {DSPType} from 'common/src/modules/dsp/dsp.types';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isCustomParamOfDateType from 'common/src/modules/campaignPlacements/utils/isCustomParamOfDateType';
import isCustomParamOfBooleanType from 'common/src/modules/campaignPlacements/utils/isCustomParamOfBooleanType';
import {DateTime} from 'luxon';

interface ValidatePlacementDataParams {
  placement: z.infer<typeof placementSchema>;
  campaignType?: CampaignType;
  campaignEnums?: Record<string, CLEnumsMapper>;
  availableDSPs?: Record<string, DSPType>;
  featureFlags?: Set<FeatureFlagKey>;
}

export default function validatePlacementData({
  placement,
  campaignType,
  campaignEnums,
  availableDSPs,
  featureFlags,
}: ValidatePlacementDataParams): {
  isValid: boolean;
  errors: string[];
} {
  const response = {
    isValid: true,
    errors: [] as string[],
  };

  if (!placement.plcId) {
    response.isValid = false;
    response.errors.push('Placement ID is required');
  }

  if (!placement.name || placement.name.trim() === '') {
    response.isValid = false;
    response.errors.push('Placement name is required');
  }

  if (!placement.dspId) {
    response.isValid = false;
    response.errors.push('DSP ID is required');
  } else if (availableDSPs && !availableDSPs[placement.dspId]) {
    response.isValid = false;
    response.errors.push(
      `DSP ID '${placement.dspId}' is not available for this campaign type. Available DSPs: ${Object.keys(availableDSPs).join(', ')}`,
    );
  }

  if (
    placement.environment &&
    !Object.values(CampaignEnvironment).includes(placement.environment)
  ) {
    response.isValid = false;
    response.errors.push('Invalid environment');
  }

  // Environment validation by campaign type
  if (campaignType !== undefined && campaignEnums && placement.environment) {
    const environmentEnums = campaignEnums[CLEnumKeys.CampaignPage.EnvironmentEnum];

    if (environmentEnums) {
      const validEnvironments = environmentEnums.enumsForSelector.filter(
        (environmentEnum) =>
          environmentEnum.campaignTypes?.length === 0 ||
          environmentEnum.campaignTypes?.includes(campaignType),
      );

      const isValidEnvironment = validEnvironments.some(
        (env) => env.name === placement.environment,
      );

      if (!isValidEnvironment) {
        response.isValid = false;
        response.errors.push(
          `Environment '${placement.environment}' is not supported for this campaign type`,
        );
      }
    }
  }

  // Ad size format validation
  if (placement.adSize && placement.adSize.trim() !== '') {
    const adSizeStr = placement.adSize;

    if (adSizeStr !== 'All') {
      const [widthStr, heightStr] = adSizeStr.split('x');

      if (!widthStr || !heightStr) {
        response.isValid = false;
        response.errors.push('Ad size must be in format WIDTHxHEIGHT (e.g., 300x250) or "All"');
      } else {
        const width = +widthStr;
        const height = +heightStr;

        if (
          isNaN(width) ||
          isNaN(height) ||
          parseInt(String(width)) !== width ||
          parseInt(String(height)) !== height ||
          width <= 0 ||
          height <= 0
        ) {
          response.isValid = false;
          response.errors.push(
            'Ad size dimensions must be positive integers (e.g., 300x250, not 300.5x250 or 0x250)',
          );
        }
      }
    }
  }

  // Custom parameters validation
  if (placement.customParameters && Object.keys(placement.customParameters).length > 0) {
    if (!campaignEnums) {
      response.isValid = false;
      response.errors.push('Campaign enums are required for custom parameters validation');

      return response;
    }

    const customParamsEnum = campaignEnums[CLEnumKeys.CampaignPage.PlacementCustomParams];

    if (!customParamsEnum) {
      response.isValid = false;
      response.errors.push('Custom parameters enum not found');

      return response;
    }

    let startDateTime: DateTime | undefined;
    let endDateTime: DateTime | undefined;

    for (const customParam of placement.customParameters) {
      // Validate key exists in enum
      if (!customParamsEnum.byName[customParam.name]) {
        response.isValid = false;
        response.errors.push(`Unknown custom parameter: '${customParam.name}'`);

        continue;
      }

      const paramOption = customParamsEnum.byName[customParam.name];

      // Check campaign type compatibility
      if (
        campaignType !== undefined &&
        paramOption.campaignTypes &&
        paramOption.campaignTypes.length > 0 &&
        !paramOption.campaignTypes?.includes(campaignType)
      ) {
        response.isValid = false;
        response.errors.push(
          `Custom parameter '${customParam.name}' is not supported for this campaign type`,
        );

        continue;
      }

      // Check date param feature flag
      if (isCustomParamOfDateType(customParam.name)) {
        if (!featureFlags?.has(FeatureFlagKey.EnablePlacementsDateParams)) {
          response.isValid = false;
          response.errors.push(
            `Date parameter '${customParam.name}' requires EnablePlacementsDateParams feature flag`,
          );

          continue;
        }

        // Validate value type
        const dateTime = DateTime.fromISO(customParam.value as string);

        if (!dateTime.isValid) {
          response.isValid = false;
          response.errors.push(`Custom parameter '${customParam.name}' must be a valid ISO string`);

          continue;
        }

        // Store for date range validation
        if (customParam.name === PlacementCustomParamName.StartDateTime) {
          startDateTime = dateTime;
        } else if (customParam.name === PlacementCustomParamName.EndDateTime) {
          endDateTime = dateTime;
        }
      } else if (isCustomParamOfBooleanType(customParam.name)) {
        // Validate boolean type
        if (typeof customParam.value !== 'boolean') {
          response.isValid = false;
          response.errors.push(`Custom parameter '${customParam.name}' must be a boolean value`);

          continue;
        }
      } else {
        // Regular string parameter
        if (typeof customParam.value !== 'string') {
          response.isValid = false;
          response.errors.push(`Custom parameter '${customParam.name}' must be a string value`);

          continue;
        }
      }
    }

    // Date range validation
    if (startDateTime && endDateTime && startDateTime >= endDateTime) {
      response.isValid = false;
      response.errors.push('End Date & Time should be greater than Start Date & Time');
    }
  }

  return response;
}
